// Step 3's contract milestones: each Schedule 1 date against the page's forecast, with what
// the forecast rests on. Variance says Late when the forecast misses the contract date, and
// At risk when something it waits on is overdue or owed by Amazon with no date: the forecast
// assumes every open step clears tomorrow at the earliest, so that is when it cannot be trusted.
import type { ReactNode } from "react";

import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import { plural } from "../projectFilesCounts";
import { type Blocker, riskCauses } from "./blockers";
import { CodBasis } from "./CodBasis";
import { codLine } from "./codLine";
import { ContractText } from "./ContractText";
import { STEP_CLAUSES } from "./contractClauses";
import { addBusinessDays, addDays, day, daysBetween, formatDate } from "./dates";
import { type Target, useEdit } from "./editContext";
import { Editable } from "./editing";
import { HoverCard } from "./HoverCard";
import { buildingKey, contractKey } from "./overrides";
import { RiskList } from "./RiskList";
import { designInputs } from "./needs";
import { stepById } from "./pathSteps";
import {
  designLength,
  DR3_BUSINESS_DAYS,
  installationStart,
  type Links,
  type Projection,
  type Scheduled,
  slipTone,
} from "./schedule";
import styles from "./amazonOverview.module.css";

/** Amazon's design approval: 90 Business Days of review, then 14 to approve. */
const DR3_PARTS = "90 to review, 14 to approve";

/** The Schedule 1 date a milestone's contract date is, as the clause list names it. */
const scheduleOne = (id: string): string =>
  (STEP_CLAUSES[id] ?? []).find((c) => c.startsWith("SPPA Sch 1")) ?? "";

interface Row {
  id: string;
  label: string;
  /** The milestone in a sentence ("the design submission"). */
  noun: string;
  due: Date | null;
  forecast: Date | null;
  /** The key that writes over the contract date: a Schedule 1 date, the committed COD, or
   * none (the MSPPA sets DR3 from the design submission). */
  key: string | null;
}

export const Milestones = ({
  b,
  plan,
  projection,
  buffer,
  blockers,
  links,
  now,
  worked,
}: {
  b: AmazonBuilding;
  plan: Record<string, Scheduled>;
  projection: Projection;
  /** The days we keep in hand before the Schedule 1 design submission date. */
  buffer: number;
  /** The blockers at the root of each chain readers see (``blockersFor``). */
  blockers: Blocker[];
  links: Links;
  now: Date;
  worked: (label: string) => Target;
}) => {
  const edit = useEdit();
  const pid = b.project_id;
  const contract = (iso: string | undefined): Date | null => (iso ? day(iso) : null);
  const dates = b.contract_dates;
  // The contract's Installation Start Date floats with design approval as forecast.
  const installStart = installationStart(b, plan.dr3.end);
  // The MSPPA's Purchaser Design Approval Date runs from the design submission itself.
  const approvalDue = plan.design.end ? addBusinessDays(plan.design.end, DR3_BUSINESS_DAYS) : null;
  const review = stepById("llsignoff");
  const sameAsDesign = Boolean(dates?.llsignoff && dates.llsignoff === dates.design);
  const approval = b.freehold ? "Amazon Real Estate approval" : "Landlord approval";
  const rows: Row[] = [
    { id: "design", label: "Detailed design submitted", noun: "the design submission", due: contract(dates?.design), forecast: plan.design.end, key: contractKey("design") },
    { id: "llsignoff", label: approval, noun: approval.toLowerCase(), due: contract(dates?.llsignoff), forecast: plan.llsignoff.end, key: contractKey("llsignoff") },
    { id: "dr3", label: "Design approval (DR3)", noun: "design approval", due: approvalDue, forecast: plan.dr3.end, key: null },
    { id: "install", label: "Installation start", noun: "installation start", due: installStart?.date ?? null, forecast: plan.install.start, key: contractKey("install") },
    { id: "cod", label: "COD", noun: "COD", due: contract(b.committed_cod), forecast: projection.date, key: buildingKey("committed_cod") },
  ];
  // A risk is named on the first milestone it reaches; the ones after it follow that one.
  const firstWith = new Map<string, Row>();
  const causesOf = new Map(rows.map((r) => [r.id, riskCauses(b, links, blockers, r.id, now)]));
  const ledBy = (r: Row): Row | null => {
    const key = causesOf.get(r.id)?.join("|") ?? "";
    if (!key) return null;
    const first = firstWith.get(key);
    if (first) return first;
    firstWith.set(key, r);
    return null;
  };
  const design = designLength(b);
  const outstanding = b.steps.design?.state === "done" ? 0 : designInputs(b).missing.length;
  const basis: Record<string, ReactNode> = {
    design: [
      `${design.days} days of design once its inputs are in (${design.basis})`,
      // The forecast plans the inputs still to come on arriving tomorrow (Noaa, 18 Oct).
      outstanding
        ? `the date assumes Amazon's ${outstanding} outstanding design inputs arrive tomorrow`
        : plan.design.start
          ? `starting ${formatDate(plan.design.start)}`
          : "",
    ]
      .filter(Boolean)
      .join("; "),
    llsignoff: review.ownEstimate
      ? `${review.days} days of ${b.freehold ? "Amazon Real Estate" : "landlord"} review: our own estimate, not from the contract`
      : "",
    dr3:
      plan.dr3.kind === "done"
        ? ""
        : !approvalDue || !plan.dr3.end || plan.dr3.end <= approvalDue
          ? "Amazon uses its full review"
          : plan.gridoffer.end && plan.gridoffer.end.getTime() === plan.dr3.end.getTime()
            ? "Amazon's full review, then waiting for the grid offer"
            : `Amazon's full ${DR3_BUSINESS_DAYS} business days, counted from tomorrow`,
    install: `${stepById("premob").days ?? 0} days of pre-mobilisation after Notice to Proceed and a ready roof`,
    cod: codLine(b, plan, projection),
  };

  return (
    <div className={styles.panel}>
      <h3>
        Contract milestones <small>Contract dates from SPPA Schedule 1 and the MSPPA</small>
      </h3>
      <div className={styles.tableScroll}>
        <table className={styles.milestones}>
          <thead>
            <tr>
              <th>Milestone</th>
              <th>Contract</th>
              <th>Forecast</th>
              <th>Variance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const { id, label, due, forecast, key } = r;
              const v = due && forecast ? daysBetween(due, forecast) : null;
              // Only the COD's lateness is toned by how far it slips; other milestones stay red.
              const nearlyOnTime = id === "cod" && v != null && v > 0 && slipTone(v) === "amber";
              // The days early apply to the design submission alone.
              const aim = id === "design" && due && buffer > 0 ? addDays(due, -buffer) : null;
              const intoBuffer = aim && forecast && v != null && v <= 0 ? daysBetween(aim, forecast) : 0;
              const causes = causesOf.get(id) ?? [];
              const leader = ledBy(r);
              const listed = causes.length > 0 && !leader;
              const source = scheduleOne(id);
              const causeList = <RiskList key={`${pid}|${id}`} causes={causes} />;
              return (
                <tr key={id}>
                  <td>{label}</td>
                  <td>
                    {edit.editing && key ? (
                      <Editable
                        target={key.startsWith("contract.") ? { kind: "contract", projectId: pid } : { kind: "building", projectId: pid }}
                        keys={[key]}
                      >
                        {due ? formatDate(due) : <span className={styles.muted}>-</span>}
                      </Editable>
                    ) : due && v != null && v > 0 ? (
                      <HoverCard
                        className={`${styles.linkButton} ${nearlyOnTime ? styles.contractRiskWarn : styles.contractRisk}`}
                        label={`${label}: contract date missed by ${v} days`}
                        trigger={formatDate(due)}
                      >
                        <div className={styles.cardTitle}>{label}</div>
                        <p>The forecast misses the contract date by {v} days.</p>
                        <ContractText ids={STEP_CLAUSES[id] ?? []} more="The contract text" />
                      </HoverCard>
                    ) : due ? (
                      formatDate(due)
                    ) : (
                      <span className={styles.muted}>-</span>
                    )}
                    {id === "llsignoff" && sameAsDesign && (
                      <HoverCard className={styles.warnMark} label={`${label}: same date as the design submission`} trigger="!">
                        <div className={styles.cardTitle}>Same date as the design submission</div>
                        <p>
                          Read literally, the {b.freehold ? "approval comes" : "landlord approves the detailed pack"} the
                          day it goes in. Worth checking against Schedule 1.
                        </p>
                      </HoverCard>
                    )}
                    {id === "install" && installStart?.floated ? (
                      <div className={styles.milestoneNote}>
                        MSPPA: 30 days after design approval, later than {source}
                        {dates?.install ? `, ${formatDate(day(dates.install))}` : ""}
                      </div>
                    ) : source ? (
                      <div className={styles.milestoneNote}>{source}</div>
                    ) : (
                      id === "dr3" && (
                        <div className={styles.milestoneNote}>
                          MSPPA Purchaser Design Approval Date: {DR3_BUSINESS_DAYS} business days
                          after the design submission ({DR3_PARTS})
                        </div>
                      )
                    )}
                    {aim && (
                      <div className={styles.milestoneNote}>
                        We aim for {formatDate(aim)}, {buffer} days early
                      </div>
                    )}
                  </td>
                  <td>
                    <Editable target={worked(`${label} forecast`)} keys={[]}>
                      {formatDate(forecast)}
                    </Editable>
                    {basis[id] && <div className={styles.milestoneNote}>{basis[id]}</div>}
                  </td>
                  <td>
                    {v != null && v > 0 ? (
                      <>
                        <span className={nearlyOnTime ? styles.lateWarn : styles.late}>
                          Projected {plural(v, "day")} late
                        </span>
                        {leader && <div className={styles.milestoneNote}>Later still if {leader.noun} slips</div>}
                        {listed && (
                          <>
                            <div className={styles.riskHead}>and at risk:</div>
                            {causeList}
                          </>
                        )}
                      </>
                    ) : intoBuffer > 0 ? (
                      <>
                        <span className={styles.atRisk}>
                          Using {intoBuffer} of {buffer} buffer days
                        </span>
                        {listed && (
                          <>
                            <div className={styles.riskHead}>and at risk:</div>
                            {causeList}
                          </>
                        )}
                      </>
                    ) : causes.length > 0 ? (
                      <>
                        <span className={styles.atRisk}>At risk</span>
                        {leader ? (
                          <div className={styles.milestoneNote}>Follows {leader.noun}, at risk above</div>
                        ) : (
                          causeList
                        )}
                      </>
                    ) : v != null ? (
                      <span className={styles.ok}>On time</span>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={styles.milestoneNote}>
        On time: the forecast holds and nothing before it is overdue. At risk: something it waits on
        is past its needed-by date, or is owed by Amazon with no date. Late: the forecast misses the
        contract date.
      </p>
      <CodBasis b={b} schedule={plan} projection={projection} plan={worked("Our plan's COD")} />
    </div>
  );
};
